import api from '@/api';

const SET_CALENDAR = 'SET_CALENDAR';
const SET_ERRORS = 'SET_ERRORS';
const RESET_ERRORS = 'RESET_ERRORS';
const UPDATE_TIME = 'UPDATE_TIME';
const SET_CURRENT_DATE_TIME = 'SET_CURRENT_DATE_TIME';
const RESET_CURRENT_DATE_TIME = 'RESET_CURRENT_DATE_TIME';
const SET_FILTER = 'SET_FILTER';
const RESET_FILTER = 'RESET_FILTER';

export default {
  namespaced: true,

  state: () => ({
    data: [],
    meta: null,
    filter: null,
    errors: [],
    currentDateTime: null,
  }),

  getters: {
    getData: ({ data, filter, currentDateTime }) => {
      if (!data?.length) return [];

      /**
       * Хелпер для фильтрации по рейтингу
       * @param {Object} timeElement
       * @returns {Object | null}
       */
      const getUiWithFilter = (timeElement) => {
        if (!timeElement || !filter) return null;

        const rating = timeElement.qualityRating?.rating;
        const isActive = rating <= filter.rating;

        return {
          ...(!isActive && { isDisabled: true }),
          ...(isActive && { isHighlighted: true }),
        };
      };

      return data.map((day) => {
        if (!day?.schedule) return day;

        const schedule = day.schedule.map((timeElement) => {
          /**
           * Установка флага активности выпадающего списка
           */
          const dropdownIsOpened = (
            day.date === currentDateTime?.date
            && timeElement.from === currentDateTime?.time
          );

          return {
            ...timeElement,
            ui: {
              ...timeElement.ui,
              dropdownIsOpened,
              ...getUiWithFilter(timeElement),
            },
          };
        });

        return {
          ...day,
          schedule,
        };
      });
    },
    getMeta: ({ meta }) => meta,
    getFilter: ({ filter }) => filter,
    getErrors: ({ errors }) => errors,
    getCurrentDateTime: ({ currentDateTime }) => currentDateTime,
    getAverageRating: ({ data }) => {
      if (!data) return 0;

      const stats = data.reduce(
        (acc, { schedule }) => (
          schedule.reduce(
            ({ count, rating }, { qualityRating }) => ({
              count: count + (qualityRating ? 1 : 0),
              rating: rating + (qualityRating?.rating || 0),
            }),
            acc,
          )
        ),
        { count: 0, rating: 0 },
      );

      return Math.round(stats.rating / stats.count) || 0;
    },
  },

  actions: {
    /**
     * @param commit
     * @param {string} userId
     * @param appUser
     * @param {string} userType
     * @param {Object} filter
     * @returns {Promise<Object>}
     */
    async fetch({ commit }, {
      userId, appUser, userType, filter,
    }) {
      const calendar = await api.getCalendar(userId, appUser, userType, filter);
      commit(SET_CALENDAR, calendar);
      return calendar;
    },

    /**
     * @param commit
     * @param {Object} filter
     * @returns {Promise<void>}
     */
    async setFilter({ commit }, filter) {
      commit(SET_FILTER, filter);
    },

    /**
     * @param commit
     * @returns {Promise<void>}
     */
    async resetFilter({ commit }) {
      commit(RESET_FILTER);
    },

    /**
     * @param commit
     * @returns {Promise<void>}
     */
    async resetErrors({ commit }) {
      commit(RESET_ERRORS);
    },

    /**
     * @param commit
     * @param {string} date
     * @param {string} time
     * @returns {Promise<void>}
     */
    async setCurrentDateTime({ commit }, { date, time }) {
      commit(SET_CURRENT_DATE_TIME, { date, time });
    },

    /**
     * @param commit
     * @returns {Promise<void>}
     */
    async resetCurrentDateTime({ commit }) {
      commit(RESET_CURRENT_DATE_TIME);
    },

    /**
     * @param commit
     * @param {Object} payload
     * @returns {Promise<boolean>}
     */
    async reserveLesson({ dispatch }, payload) {
      const request = await api.reserveLesson(payload);
      return dispatch('updateDataHelper', request);
    },

    /**
     * @param commit
     * @param {Object} payload
     * @returns {Promise<boolean>}
     */
    async reserveLessonByAdmin({ dispatch }, payload) {
      const request = await api.reserveLessonByAdmin(payload);
      return dispatch('updateDataHelper', request);
    },

    /**
     * @param commit
     * @param {Object} payload
     * @returns {Promise<boolean>}
     */
    async refuseLesson({ dispatch }, payload) {
      const request = await api.refuseLesson(payload);
      return dispatch('updateDataHelper', request);
    },

    /**
     * @param commit
     * @param {Object} payload
     * @returns {Promise<boolean>}
     */
    async refuseLessonByAdmin({ dispatch }, payload) {
      const request = await api.refuseLessonByAdmin(payload);
      return dispatch('updateDataHelper', request);
    },

    /**
     * @param commit
     * @param {Object} payload
     * @returns {Promise<boolean>}
     */
    async blockLesson({ dispatch }, payload) {
      const request = await api.blockLesson(payload);
      return dispatch('updateDataHelper', request);
    },

    /**
     * @param commit
     * @param {Object} payload
     * @returns {Promise<boolean>}
     */
    async blockCashing({ dispatch }, payload) {
      const request = await api.blockCashing(payload);
      return dispatch('updateDataHelper', request);
    },

    /**
     * @param commit
     * @param {Object} payload
     * @returns {Promise<boolean>}
     */
    async unblockCashing({ dispatch }, payload) {
      const request = await api.unblockCashing(payload);
      return dispatch('updateDataHelper', request);
    },

    /**
     * @param commit
     * @param {Object} payload
     * @returns {Promise<boolean>}
     */
    async cancelEvent({ dispatch }, payload) {
      const request = await api.cancelEvent(payload);
      return dispatch('updateDataHelper', request);
    },

    /**
     * @param commit
     * @param {Object} payload
     * @returns {Promise<boolean>}
     */
    async updateRating({ dispatch }, payload) {
      const request = await api.updateRating(payload);
      return dispatch('updateDataHelper', request);
    },

    /**
     * @param commit
     * @param {Object} request
     * @returns {Promise<boolean>}
     */
    async updateDataHelper({ commit }, request) {
      const { errors, data, meta } = request;
      if (errors) {
        commit(SET_ERRORS, errors);
        return false;
      }

      commit(SET_CALENDAR, { data, meta });
      return Boolean(data);
    },
  },

  mutations: {
    [SET_CALENDAR](state, { data, meta }) {
      state.data = data;
      state.meta = meta;
    },

    [SET_FILTER](state, filter) {
      state.filter = filter;
    },

    [RESET_FILTER](state) {
      state.filter = null;
    },

    [SET_ERRORS](state, errors) {
      state.errors = errors;
    },

    [RESET_ERRORS](state) {
      state.errors.splice(0, state.errors.length);
    },

    [UPDATE_TIME](state, { indexElement, timeIndex, timeElement }) {
      state.data[indexElement].schedule.splice(timeIndex, 1, timeElement);
    },

    [SET_CURRENT_DATE_TIME](state, { date, time }) {
      state.currentDateTime = {
        date,
        time,
      };
    },

    [RESET_CURRENT_DATE_TIME](state) {
      state.currentDateTime = null;
    },
  },
};
